/**
 * Support Service
 * Communicates with Jupiter backend API for creating support tickets
 */

import { apiConfig } from '../config/app';

const API_URL = apiConfig.supportApiUrl;
const API_KEY = apiConfig.supportApiKey;

/**
 * Create a support ticket
 * @param {Object} ticketData - Ticket data
 * @param {string} ticketData.message - User's message (required)
 * @param {string} ticketData.subject - Subject (optional, auto-generated if not provided)
 * @param {Object} ticketData.user - User information (required)
 * @param {string} ticketData.user.uid - User UID (required)
 * @param {string} ticketData.user.email - User email (required)
 * @param {string} ticketData.user.displayName - User display name (optional)
 * @param {string} ticketData.user.role - User role (optional)
 * @returns {Promise<Object>} Response with ticketId
 */
export async function createSupportTicket(ticketData) {
  // Validate configuration
  if (!API_KEY) {
    throw new Error('Jupiter API key is not configured. Please set REACT_APP_JUPITER_API_KEY environment variable.');
  }

  if (!API_URL) {
    throw new Error('Jupiter API URL is not configured. Please set REACT_APP_JUPITER_API_URL environment variable.');
  }

  // Validate required fields
  if (!ticketData.message || typeof ticketData.message !== 'string') {
    throw new Error('Message is required');
  }

  if (!ticketData.user || !ticketData.user.uid || !ticketData.user.email) {
    throw new Error('User information is required (uid and email)');
  }

  // Prepare request
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.support);

  try {
    const response = await fetch(`${API_URL}/supportTicketsCreate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      },
      body: JSON.stringify({
        message: ticketData.message,
        subject: ticketData.subject,
        user: {
          uid: ticketData.user.uid,
          email: ticketData.user.email,
          displayName: ticketData.user.displayName || '',
          role: ticketData.user.role || ''
        }
      }),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
      
      if (response.status === 401) {
        throw new Error('Invalid API key. Please check your configuration.');
      } else if (response.status === 400) {
        throw new Error(errorMessage);
      } else {
        throw new Error(`Failed to create ticket: ${errorMessage}`);
      }
    }

    const data = await response.json();
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    
    if (error.message) {
      throw error;
    }
    
    console.error('Error creating support ticket:', error);
    throw new Error('Failed to create support ticket. Please check your connection and try again.');
  }
}

/**
 * Get a support ticket by ID (for polling agent replies)
 * @param {string} ticketId - The ticket ID
 * @returns {Promise<Object>} { success, ticket: { id, messages, ... } }
 */
export async function getSupportTicket(ticketId) {
  if (!API_KEY) {
    throw new Error('Jupiter API key is not configured.');
  }
  if (!API_URL) {
    throw new Error('Jupiter API URL is not configured.');
  }
  if (!ticketId || typeof ticketId !== 'string') {
    throw new Error('Ticket ID is required');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.support);

  try {
    // API key sent as query param (not header) so the GET is a "simple" request with no CORS preflight.
    const url = `${API_URL}/supportTicketsGet?ticketId=${encodeURIComponent(ticketId)}&apiKey=${encodeURIComponent(API_KEY)}`;
    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    throw error;
  }
}

/**
 * Add a message to an existing support ticket
 * @param {string} ticketId - The ticket ID to add the message to
 * @param {string} message - The message text
 * @param {Object} user - User information
 * @returns {Promise<Object>} Response
 */
export async function addMessageToTicket(ticketId, message, user) {
  // Validate configuration
  if (!API_KEY) {
    throw new Error('Jupiter API key is not configured.');
  }

  if (!API_URL) {
    throw new Error('Jupiter API URL is not configured.');
  }

  // Validate required fields
  if (!ticketId || typeof ticketId !== 'string') {
    throw new Error('Ticket ID is required');
  }

  if (!message || typeof message !== 'string') {
    throw new Error('Message is required');
  }

  if (!user || !user.uid || !user.email) {
    throw new Error('User information is required (uid and email)');
  }

  // Prepare request
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.support);

  try {
    const response = await fetch(`${API_URL}/supportTicketsAddMessage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      },
      body: JSON.stringify({
        ticketId,
        message,
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          role: user.role || ''
        }
      }),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
      throw new Error(`Failed to add message: ${errorMessage}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    
    if (error.message) {
      throw error;
    }
    
    console.error('Error adding message to support ticket:', error);
    throw new Error('Failed to add message. Please check your connection and try again.');
  }
}

